import type { TemplateRegistration, TemplateData } from './types';
import { sanitizeText, sanitizeSeoText, escapeAttr } from './sanitize';
import { getIconSvg, renderStars, buildFontsUrl, buildNavLinks, NavDataFlags, truncateAtSentence } from './helpers';

export const warmArtisan: TemplateRegistration = {
  meta: {
    id: 'warm-artisan',
    name: 'Warm Artisan',
    category: 'Creative',
    description: 'Handcrafted, warm boutique design with serif typography and earthy terracotta accents',
    previewImage: 'https://images.unsplash.com/photo-1513519245088-0e12902e35ca?w=800&q=80',
    supportedFeatures: ['contact-form', 'phone-display', 'address-map', 'business-hours', 'about-section', 'services-list', 'testimonials', 'google-reviews', 'social-links', 'faq', 'price-list', 'promotions', 'photo-gallery'],
    defaultEnabledFeatures: ['contact-form', 'phone-display', 'about-section', 'services-list', 'testimonials'],
  },
  render(data: TemplateData, enabledFeatures: Set<string>): string {
    const safeName = sanitizeText(data.businessName);
    const safeHeadline = sanitizeText(data.headline);
    const safeAbout = sanitizeText(data.aboutText);
    const safeSeoTitle = sanitizeSeoText(data.seo.title, 70);
    const safeSeoDesc = sanitizeSeoText(data.seo.description, 160);

    const phone = data.businessData?.phone || null;
    const contactEmail = data.businessData?.contactEmail || null;
    const rating = data.businessData?.rating || null;
    const reviewCount = data.businessData?.reviewCount || 0;
    const businessHours = data.businessData?.businessHours || [];
    const photos = data.businessData?.photos || [];
    const address = data.businessData?.address || '';
    const googleMapsUrl = data.businessData?.googleMapsUrl || null;
    const galleryImages = photos.slice(1, 6);

    const navDataFlags: NavDataFlags = {
      hasServices: data.services.length > 0,
      hasTestimonials: data.testimonials.length > 0,
      hasGalleryPhotos: galleryImages.length > 0,
      hasFaq: !!(data.faq && data.faq.length > 0),
      hasPriceList: !!(data.priceList && data.priceList.length > 0),
    };
    const navLinks = buildNavLinks(enabledFeatures, navDataFlags);
    const fontsUrl = buildFontsUrl(['Cormorant Garamond', 'EB Garamond']);

    // Split headline for accent word styling
    const headlineWords = safeHeadline.split(' ');
    const accentIndex = Math.min(1, headlineWords.length - 1);
    const styledHeadline = headlineWords.map((w, i) => i === accentIndex ? `<span class="accent-word">${w}</span>` : w).join(' ');

    const sections: string[] = [];

    // --- Google Reviews ---
    if (enabledFeatures.has('google-reviews') && rating) {
      sections.push(`
      <section class="google-reviews">
        <div class="container">
          <div class="review-badge">
            <div class="review-stars">${renderStars(Math.round(rating))}</div>
            <span class="review-text">${rating.toFixed(1)} out of 5 &mdash; ${reviewCount} Google reviews</span>
          </div>
        </div>
      </section>`);
    }

    // --- Promotions ---
    if (enabledFeatures.has('promotions') && data.promotion) {
      const promoTitle = sanitizeText(data.promotion.title);
      const promoDesc = sanitizeText(data.promotion.description);
      const promoDiscount = sanitizeText(data.promotion.discount);
      sections.push(`
      <section class="promo-banner">
        <div class="container">
          <div class="promo-inner">
            <span class="promo-badge">${promoDiscount}</span>
            <div class="promo-content">
              <strong>${promoTitle}</strong>
              <span>${promoDesc}</span>
            </div>
          </div>
        </div>
      </section>`);
    }

    // --- About ---
    if (enabledFeatures.has('about-section')) {
      sections.push(`
      <section id="about" class="section about-section">
        <div class="about-band">
          <div class="container about-inner">
            <h2 class="section-title">Our Story</h2>
            <div class="decorative-dash">&mdash;&mdash;&mdash;</div>
            <div class="about-bordered">
              <p class="about-text">${safeAbout}</p>
            </div>
          </div>
        </div>
      </section>`);
    }

    // --- Services ---
    if (enabledFeatures.has('services-list') && data.services.length > 0) {
      const serviceCards = data.services.map((s) => {
        const sName = sanitizeText(s.name);
        const sDesc = sanitizeText(s.description);
        return `
          <div class="service-card">
            <div class="service-icon">${getIconSvg(s.icon || 'star')}</div>
            <h3>${sName}</h3>
            <p>${sDesc}</p>
            ${s.price ? `<span class="service-price">${sanitizeText(s.price)}</span>` : ''}
          </div>`;
      }).join('');
      sections.push(`
      <section id="services" class="section">
        <div class="container">
          <h2 class="section-title">What We Offer</h2>
          <div class="decorative-dash">&mdash;&mdash;&mdash;</div>
          <div class="services-grid">${serviceCards}</div>
        </div>
      </section>`);
    }

    // --- Photo Gallery ---
    if (enabledFeatures.has('photo-gallery') && galleryImages.length > 0) {
      const imgs = galleryImages.map((url, i) => {
        const rotation = (i % 2 === 0 ? -1.5 : 1.5);
        return `<div class="gallery-item" style="--rot: ${rotation}deg"><img src="${escapeAttr(url)}" alt="${safeName}" loading="lazy"></div>`;
      }).join('');
      sections.push(`
      <section id="gallery" class="section">
        <div class="container">
          <h2 class="section-title">Gallery</h2>
          <div class="decorative-dash">&mdash;&mdash;&mdash;</div>
          <div class="gallery-grid">${imgs}</div>
        </div>
      </section>`);
    }

    // --- Testimonials ---
    if (enabledFeatures.has('testimonials') && data.testimonials.length > 0) {
      const featured = data.testimonials[0];
      const rest = data.testimonials.slice(1);
      const fName = sanitizeText(featured.name);
      const fContent = sanitizeText(featured.content);
      const fRole = featured.role ? sanitizeText(featured.role) : '';
      const restCards = rest.map(t => {
        const tName = sanitizeText(t.name);
        const tContent = sanitizeText(t.content);
        const tRole = t.role ? sanitizeText(t.role) : '';
        return `
          <div class="testimonial-card">
            <p class="testimonial-body">&ldquo;${tContent}&rdquo;</p>
            <div class="testimonial-author">
              <span class="author-dash">&mdash;</span>
              <strong>${tName}</strong>
              ${tRole ? `<span class="testimonial-role">, ${tRole}</span>` : ''}
            </div>
            ${t.rating ? `<div class="testimonial-stars">${renderStars(t.rating)}</div>` : ''}
          </div>`;
      }).join('');
      sections.push(`
      <section id="testimonials" class="section">
        <div class="container">
          <h2 class="section-title">Kind Words</h2>
          <div class="decorative-dash">&mdash;&mdash;&mdash;</div>
          <div class="testimonial-featured">
            <p class="testimonial-body">&ldquo;${fContent}&rdquo;</p>
            <div class="testimonial-author">
              <span class="author-dash">&mdash;</span>
              <strong>${fName}</strong>
              ${fRole ? `<span class="testimonial-role">, ${fRole}</span>` : ''}
            </div>
            ${featured.rating ? `<div class="testimonial-stars">${renderStars(featured.rating)}</div>` : ''}
          </div>
          ${rest.length > 0 ? `<div class="testimonials-rest">${restCards}</div>` : ''}
        </div>
      </section>`);
    }

    // --- FAQ ---
    if (enabledFeatures.has('faq') && data.faq && data.faq.length > 0) {
      const items = data.faq.map(f => {
        const q = sanitizeText(f.question);
        const a = sanitizeText(f.answer);
        return `
          <div class="faq-item">
            <h3 class="faq-q">${q}</h3>
            <p class="faq-a">${a}</p>
          </div>`;
      }).join('');
      sections.push(`
      <section id="faq" class="section">
        <div class="container">
          <h2 class="section-title">Questions &amp; Answers</h2>
          <div class="decorative-dash">&mdash;&mdash;&mdash;</div>
          <div class="faq-list">${items}</div>
        </div>
      </section>`);
    }

    // --- Price List ---
    if (enabledFeatures.has('price-list') && data.priceList && data.priceList.length > 0) {
      const rows = data.priceList.map(p => {
        const pName = sanitizeText(p.name);
        const pDesc = sanitizeText(p.description);
        const pPrice = sanitizeText(p.price);
        return `
          <div class="price-row">
            <div class="price-info">
              <h3>${pName}</h3>
              <p>${pDesc}</p>
            </div>
            <div class="price-dots"></div>
            <span class="price-amount">${pPrice}</span>
          </div>`;
      }).join('');
      sections.push(`
      <section id="pricing" class="section">
        <div class="container">
          <h2 class="section-title">Pricing</h2>
          <div class="decorative-dash">&mdash;&mdash;&mdash;</div>
          <div class="price-list">${rows}</div>
        </div>
      </section>`);
    }

    // --- Business Hours ---
    if (enabledFeatures.has('business-hours') && businessHours.length > 0) {
      const hoursList = businessHours.map(h => `<li>${sanitizeText(h)}</li>`).join('');
      sections.push(`
      <section class="section">
        <div class="container">
          <h2 class="section-title">Visit Us</h2>
          <div class="decorative-dash">&mdash;&mdash;&mdash;</div>
          <ul class="hours-list">${hoursList}</ul>
        </div>
      </section>`);
    }

    // --- Contact ---
    if (enabledFeatures.has('contact-form')) {
      sections.push(`
      <section id="contact" class="section contact-section">
        <div class="container">
          <h2 class="section-title">Get In Touch</h2>
          <div class="decorative-dash">&mdash;&mdash;&mdash;</div>
          <div class="contact-stacked">
            <div class="contact-info">
              ${enabledFeatures.has('phone-display') && phone ? `<div class="contact-item">${getIconSvg('phone')}<a href="tel:${escapeAttr(phone)}">${sanitizeText(phone)}</a></div>` : ''}
              ${enabledFeatures.has('address-map') && address ? `<div class="contact-item">${getIconSvg('map-pin')}<span>${sanitizeText(address)}</span></div>` : ''}
              ${enabledFeatures.has('address-map') && googleMapsUrl ? `<a class="map-link" href="${escapeAttr(googleMapsUrl)}" target="_blank" rel="noopener noreferrer">View on Google Maps &rarr;</a>` : ''}
            </div>
            <form class="contact-form" ${contactEmail ? `action="https://formsubmit.co/${escapeAttr(contactEmail)}" method="POST"` : 'onsubmit="return false;"'}>
              <input type="text" placeholder="Your Name" required>
              <input type="email" placeholder="Your Email" required>
              <textarea placeholder="Your Message" rows="5" required></textarea>
              <button type="submit">Send Message</button>
            </form>
          </div>
        </div>
      </section>`);
    }

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${safeSeoTitle}</title>
  <meta name="description" content="${safeSeoDesc}">
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="${fontsUrl}" rel="stylesheet">
  <style>
    *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
    :root {
      --bg: #fefdf8; --bg-warm: #faf5eb; --bg-band: #f5efe3;
      --text: #422006; --text-muted: #78716c;
      --accent: #c2410c; --green: #65a30d;
      --font-heading: 'Cormorant Garamond', Georgia, serif;
      --font-body: 'EB Garamond', Georgia, serif;
    }
    html { scroll-behavior: smooth; }
    body {
      font-family: var(--font-body); background: var(--bg); color: var(--text); line-height: 1.7;
      background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23d6d3cd' fill-opacity='0.15'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
    }
    .container { max-width: 1060px; margin: 0 auto; padding: 0 24px; }
    a { color: var(--accent); text-decoration: none; transition: opacity .2s; }
    a:hover { opacity: .8; }

    .decorative-dash { color: var(--accent); font-size: 1.2rem; letter-spacing: 4px; margin-bottom: 36px; }

    /* Wavy SVG underline */
    .wavy-underline {
      display: block; margin-top: 8px;
      background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='12' viewBox='0 0 120 12'%3E%3Cpath d='M0 6 Q15 0 30 6 Q45 12 60 6 Q75 0 90 6 Q105 12 120 6' fill='none' stroke='%23c2410c' stroke-width='2.5'/%3E%3C/svg%3E") repeat-x left bottom;
      background-size: 120px 12px; padding-bottom: 14px;
    }

    /* NAV */
    .navbar {
      position: fixed; top: 0; left: 0; right: 0; z-index: 100;
      background: rgba(254,253,248,.95); backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px);
      border-bottom: 1px solid #e7e5e4;
    }
    .nav-inner { display: flex; align-items: center; justify-content: space-between; padding: 18px 24px; max-width: 1060px; margin: 0 auto; }
    .nav-brand { font-family: var(--font-heading); font-size: 1.6rem; font-weight: 700; color: var(--text); font-style: italic; }
    .nav-links { display: flex; gap: 32px; list-style: none; }
    .nav-links a { color: var(--text-muted); font-size: .92rem; font-family: var(--font-body); font-weight: 500; transition: color .2s; }
    .nav-links a:hover { color: var(--accent); }
    .nav-toggle { display: none; background: none; border: none; cursor: pointer; }
    .nav-toggle span { display: block; width: 24px; height: 2px; background: var(--accent); margin: 5px 0; border-radius: 1px; }

    /* HERO - large watermark text */
    .hero { min-height: 100vh; display: flex; align-items: center; padding-top: 80px; position: relative; overflow: hidden; }
    .hero-watermark { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-family: var(--font-heading); font-size: 15vw; font-weight: 700; color: var(--text); opacity: .06; white-space: nowrap; pointer-events: none; z-index: 0; text-transform: uppercase; }
    .hero-inner { position: relative; z-index: 1; max-width: 800px; margin: 0 auto; padding: 80px 24px; width: 100%; text-align: center; }
    .hero-inner h1 {
      font-family: var(--font-heading); font-size: clamp(2.4rem, 5.5vw, 3.8rem); font-weight: 700;
      line-height: 1.15; margin-bottom: 20px; color: var(--text);
    }
    .hero-inner h1 .accent-word { color: var(--accent); font-style: italic; }
    .hero-inner h1 .wavy-underline { display: inline; }
    .hero-sub { font-size: clamp(1rem, 1.6vw, 1.15rem); color: var(--text-muted); max-width: 560px; margin: 0 auto 32px; line-height: 1.8; }
    .hero-cta {
      display: inline-block; padding: 14px 36px;
      background: var(--accent); color: #fff; border-radius: 40px;
      font-family: var(--font-heading); font-size: 1.05rem; font-weight: 600;
      transition: transform .2s, box-shadow .2s;
    }
    .hero-cta:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(194,65,12,.25); opacity: 1; color: #fff; }

    /* SECTIONS */
    .section { padding: 88px 0; }
    .section-title { font-family: var(--font-heading); font-size: clamp(1.8rem, 4vw, 2.6rem); font-weight: 700; margin-bottom: 8px; }

    /* ABOUT - centered with decorative borders */
    .about-section { padding: 0; }
    .about-band { background: var(--bg-band); padding: 88px 0; }
    .about-inner { max-width: 680px; margin: 0 auto; text-align: center; }
    .about-bordered { border-top: 2px solid var(--accent); border-bottom: 2px solid var(--accent); padding: 32px 0; position: relative; }
    .about-bordered::before, .about-bordered::after { content: ''; position: absolute; left: 50%; transform: translateX(-50%); width: 60px; height: 2px; background: var(--accent); }
    .about-bordered::before { top: 8px; }
    .about-bordered::after { bottom: 8px; }
    .about-text { font-size: 1.1rem; color: var(--text-muted); line-height: 1.9; }

    /* SERVICES - icon-top cards */
    .services-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 28px; }
    .service-card {
      text-align: center; padding: 36px 24px; background: var(--bg);
      border: 1px solid #e7e5e4; border-radius: 12px; transition: transform .3s, box-shadow .3s;
    }
    .service-card:hover { transform: translateY(-4px); box-shadow: 0 8px 24px rgba(66,32,6,.1); }
    .service-card .service-icon { margin-bottom: 16px; }
    .service-card .icon-char { font-size: 2rem; }
    .service-card h3 { font-family: var(--font-heading); font-size: 1.2rem; font-weight: 700; margin-bottom: 8px; }
    .service-card p { color: var(--text-muted); font-size: .95rem; line-height: 1.7; }
    .service-price { display: inline-block; margin-top: 10px; color: var(--accent); font-weight: 700; font-size: 1.05rem; }

    /* GALLERY */
    .gallery-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
    .gallery-item img {
      width: 100%; height: 220px; object-fit: cover; display: block;
      border-radius: 12px; transform: rotate(var(--rot, 0deg));
      transition: transform .3s; border: 3px solid var(--bg-band);
      box-shadow: 0 6px 20px rgba(66,32,6,.1);
    }
    .gallery-item:hover img { transform: rotate(0deg) scale(1.03); }

    /* TESTIMONIALS - single large featured quote */
    .testimonial-featured { max-width: 700px; margin: 0 auto; text-align: center; padding: 40px 32px; }
    .testimonial-featured .testimonial-body { font-family: var(--font-heading); font-size: 1.5rem; font-style: italic; color: var(--text); line-height: 1.6; margin-bottom: 24px; }
    .testimonial-featured .testimonial-author { color: var(--text-muted); font-size: 1rem; }
    .testimonial-featured .testimonial-author strong { color: var(--text); }
    .author-dash { color: var(--accent); margin-right: 4px; }
    .testimonial-role { color: var(--text-muted); }
    .testimonial-featured .testimonial-stars { margin-top: 12px; }
    .testimonials-rest { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 24px; margin-top: 40px; max-width: 900px; margin-left: auto; margin-right: auto; }
    .testimonials-rest .testimonial-card { text-align: center; padding: 24px 20px; border-top: 2px solid #e7e5e4; }
    .testimonials-rest .testimonial-body { font-family: var(--font-heading); font-size: 1rem; font-style: italic; color: var(--text); line-height: 1.7; margin-bottom: 12px; }
    .testimonials-rest .testimonial-author { color: var(--text-muted); font-size: .88rem; }
    .testimonials-rest .testimonial-author strong { color: var(--text); }
    .star { color: #d6d3d1; font-size: 1rem; }
    .star.filled { color: var(--accent); }
    .testimonial-stars { margin-top: 8px; }

    /* FAQ */
    .faq-list { max-width: 700px; }
    .faq-item { padding: 24px 0; border-bottom: 1px solid #e7e5e4; }
    .faq-q { font-family: var(--font-heading); font-size: 1.15rem; font-weight: 700; margin-bottom: 8px; }
    .faq-a { color: var(--text-muted); line-height: 1.8; font-size: .95rem; }

    /* PRICE LIST */
    .price-list { max-width: 700px; }
    .price-row { display: flex; align-items: baseline; padding: 20px 0; border-bottom: 1px solid #e7e5e4; gap: 12px; }
    .price-info { flex-shrink: 0; }
    .price-info h3 { font-family: var(--font-heading); font-size: 1.1rem; font-weight: 700; margin-bottom: 2px; }
    .price-info p { color: var(--text-muted); font-size: .88rem; }
    .price-dots { flex: 1; border-bottom: 2px dotted #d6d3d1; margin-bottom: 4px; min-width: 40px; }
    .price-amount { font-family: var(--font-heading); font-size: 1.3rem; font-weight: 700; color: var(--accent); white-space: nowrap; }

    /* HOURS */
    .hours-list { list-style: none; max-width: 380px; }
    .hours-list li { padding: 10px 0; border-bottom: 1px solid #e7e5e4; color: var(--text-muted); font-size: .93rem; }

    /* GOOGLE REVIEWS */
    .google-reviews { background: var(--bg-band); padding: 16px 0; }
    .review-badge { display: flex; align-items: center; justify-content: center; gap: 12px; flex-wrap: wrap; }
    .review-badge .star.filled { color: var(--accent); }
    .review-text { color: var(--text-muted); font-weight: 600; font-size: .9rem; }

    /* PROMO */
    .promo-banner { background: var(--bg-warm); padding: 18px 0; border-bottom: 1px solid #e7e5e4; }
    .promo-inner { display: flex; align-items: center; gap: 18px; flex-wrap: wrap; }
    .promo-badge { background: var(--accent); color: #fff; padding: 6px 18px; border-radius: 20px; font-family: var(--font-heading); font-weight: 700; font-size: .95rem; }
    .promo-content strong { display: block; margin-bottom: 2px; font-family: var(--font-heading); }
    .promo-content span { color: var(--text-muted); font-size: .88rem; }

    /* CONTACT - map + form stacked */
    .contact-section { background: var(--bg-warm); }
    .contact-stacked { display: flex; flex-direction: column; gap: 32px; max-width: 700px; margin: 0 auto; }
    .contact-info { display: flex; flex-direction: column; gap: 20px; text-align: center; align-items: center; }
    .contact-item { display: flex; align-items: center; gap: 14px; color: var(--text-muted); }
    .contact-item .icon-char { color: var(--accent); flex-shrink: 0; font-size: 20px; line-height: 1; }
    .contact-item a { color: var(--text); }
    .map-link { margin-top: 8px; font-weight: 600; font-size: .88rem; }
    .contact-form { display: flex; flex-direction: column; gap: 14px; }
    .contact-form input, .contact-form textarea {
      background: var(--bg); border: 1px solid #d6d3d1; border-radius: 8px;
      color: var(--text); padding: 14px 16px; font-family: var(--font-body); font-size: .95rem; outline: none; transition: border-color .2s;
    }
    .contact-form input:focus, .contact-form textarea:focus { border-color: var(--accent); }
    .contact-form button {
      padding: 14px 32px; background: var(--accent); color: #fff; border: none;
      border-radius: 40px; font-family: var(--font-heading); font-size: 1.05rem; font-weight: 600;
      cursor: pointer; transition: transform .2s, box-shadow .2s;
    }
    .contact-form button:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(194,65,12,.2); }

    /* FOOTER */
    .footer { background: var(--text); padding: 48px 0; text-align: center; }
    .footer-brand { font-family: var(--font-heading); font-size: 1.3rem; font-weight: 700; color: var(--bg); font-style: italic; margin-bottom: 8px; }
    .footer p { color: rgba(254,253,248,.6); font-size: .85rem; }

    /* RESPONSIVE */
    @media (max-width: 768px) {
      .nav-links { display: none; flex-direction: column; position: absolute; top: 100%; left: 0; right: 0; background: rgba(254,253,248,.98); padding: 16px 24px; gap: 16px; }
      .nav-links.show { display: flex; }
      .nav-toggle { display: block; }
      .services-grid { grid-template-columns: 1fr; }
      .gallery-grid { grid-template-columns: repeat(2, 1fr); }
      .testimonials-rest { grid-template-columns: 1fr; }
      .price-row { flex-wrap: wrap; }
      .price-dots { display: none; }
    }
    @media (max-width: 480px) {
      .gallery-grid { grid-template-columns: 1fr; }
      .hero-inner { padding: 80px 16px 40px; }
    }
  </style>
</head>
<body>
  <nav class="navbar">
    <div class="nav-inner">
      <a href="#" class="nav-brand">${safeName}</a>
      <button class="nav-toggle" aria-label="Toggle menu" onclick="this.parentElement.querySelector('.nav-links').classList.toggle('show')">
        <span></span><span></span><span></span>
      </button>
      <ul class="nav-links">
        ${navLinks.map(l => `<li><a href="${l.href}">${l.label}</a></li>`).join('')}
      </ul>
    </div>
  </nav>

  <section class="hero">
    <div class="hero-watermark">${safeName}</div>
    <div class="hero-inner">
      <h1><span class="wavy-underline">${styledHeadline}</span></h1>
      <p class="hero-sub">${truncateAtSentence(safeAbout)}</p>
      ${enabledFeatures.has('contact-form') ? `<a href="#contact" class="hero-cta">Get In Touch</a>` : ''}
    </div>
  </section>

  ${sections.join('\n')}

  <footer class="footer">
    <div class="container">
      <div class="footer-brand">${safeName}</div>
      <p>${safeName}</p>
    </div>
  </footer>
</body>
</html>`;
  }
};
